import { AuthRequest, Method } from 'src/util/Request';
import { baseUrl, genericNetworkErrorHandler } from '.';
import { useQuery, useMutation, useQueries } from '@tanstack/react-query';
import { CustomerInfo } from '@lanterds/types';
import { FetchedUserPermissions, UserForm } from 'src/components/Views/AdminView/Users/types';

export const getUsers = () => {
    const url = `${baseUrl}/keycloak/users`;
    const results = useQuery({
        queryKey: [url],
        queryFn: async () => {
            const res = await AuthRequest(url, Method.GET);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
    });
    return results;
};

export const getUser = (userId: string) => {
    const url = `${baseUrl}/keycloak/user/${userId}`;
    const results = useQuery({
        queryKey: [url],
        queryFn: async () => {
            const res = await AuthRequest(url, Method.GET);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
    });
    return results;
};

export const editUser = (userInfo: UserForm, isNewUser: boolean) => {
    const url = `${baseUrl}/keycloak/user`;
    const headers = {
        'Content-Type': 'application/json',
    };
    const results = useMutation({
        mutationKey: [url, { ...userInfo, userId: userInfo.email }],
        mutationFn: async (userInfo: UserForm) => {
            const res = await AuthRequest(url, isNewUser ? Method.POST : Method.PUT, headers, {
                ...userInfo,
                userId: userInfo.email,
            });

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: false,
    });
    return results;
};

export const deactivateUser = () => {
    const url = `${baseUrl}/keycloak/user`;

    const results = useMutation({
        mutationKey: [url],
        mutationFn: async (variables: { userId: string }) => {
            const { userId } = variables;
            const res = await AuthRequest(`${url}/${userId}`, Method.DELETE);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const deleteUser = () => {
    const url = `${baseUrl}/admin/user`;

    const results = useMutation({
        mutationKey: [url],
        mutationFn: async (variables: { userId: string }) => {
            const { userId } = variables;
            const res = await AuthRequest(`${url}/${userId}`, Method.DELETE);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const enableUser = () => {
    const url = `${baseUrl}/admin/enable/user`;
    const headers = {
        'Content-Type': 'application/json',
    };
    const results = useMutation({
        mutationKey: [url],
        mutationFn: async (user: any) => {
            const res = await AuthRequest(url, Method.PUT, headers, user);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const editUserPermissions = (email: string) => {
    const url = `${baseUrl}/user/${email}/permissions`;
    const headers = {
        'Content-Type': 'application/json',
    };
    const results = useMutation({
        mutationKey: [url, email],
        mutationFn: async (permissions: any) => {
            const res = await AuthRequest(url, Method.POST, headers, permissions);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const getUserPermissions = (userId: string, enabled = true, queryKey?: any[]) => {
    const url = `${baseUrl}/userManagement/user/${userId}/permissions`;
    const results = useQuery<FetchedUserPermissions>({
        queryKey: queryKey ? [url, ...queryKey] : [url],
        queryFn: async () => {
            const res = await AuthRequest(url, Method.GET);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
        enabled,
    });
    return results;
};

export const getAdminPermissions = (userId: string, enabled = true, queryKey?: any[]) => {
    const url = `${baseUrl}/user/${userId}/permissions`;
    const results = useQuery({
        queryKey: queryKey ? [url, ...queryKey] : [url],
        queryFn: async () => {
            const res = await AuthRequest(url, Method.GET);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
        enabled,
    });
    return results;
};

export const getDealers = (customers: CustomerInfo[], enabled?: boolean) => {
    const queryFn = async (url: string) => {
        const res = await AuthRequest(url, Method.GET);

        if (res.ok) {
            const details = await res.json();
            return details;
        } else {
            await genericNetworkErrorHandler(res);
        }
    };

    const results = useQueries({
        queries: customers.map((customer) => ({
            queryKey: [customer.customerId],
            queryFn: () => queryFn(`${baseUrl}/locations/dealers/${customer.customerId}`),
            refetchOnWindowFocus: false,
            throwOnError: true,
            enabled,
        })),
        combine: (results) => {
            return {
                data: results.map((result) => result.data),
                isFetching: results.some((result) => result.isFetching),
                isError: results.some((result) => result.isError),
                error: results.some((result) => result.error),
            };
        },
    });
    return results;
};

export const passwordReset = (userId: string) => {
    const url = `${baseUrl}/keycloak/passwordReset/${userId}`;
    const headers = {
        'Content-Type': 'application/json',
    };
    const results = useMutation({
        mutationKey: [url, userId],
        mutationFn: async () => {
            const res = await AuthRequest(url, Method.POST, headers);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};
